import { element } from 'prop-types'
import { jsx } from 'slate-hyperscript'
import { LIST_TYPES } from '../../../Element/list/utils'
import { v4 } from 'uuid'
import { cloneDeep, isEmpty } from 'lodash'
import { spanningAttrs, emptyCell, addVoidCellsAndApplyAttributes, getTbodyChildren } from './defaultDeserializer'
import { getCustomToolbarComponents } from '../utils'

const ELEMENT_TAGS = {
  A: el => ({ type: 'a', attrs: { url: el.getAttribute('href') || "#" } }),
  H1: () => ({ type: 'h1', attrs: {} }),
  H2: () => ({ type: 'h2', attrs: {} }),
  H3: () => ({ type: 'h3', attrs: {} }),
  H4: () => ({ type: 'h4', attrs: {} }),
  H5: () => ({ type: 'h5', attrs: {} }),
  H6: () => ({ type: 'h6', attrs: {} }),
  IMG: (el) => {
    return { type: 'img', attrs: { url: el.getAttribute('src'), width: 100 } }
  },
  LI: () => ({ type: 'li', attrs: {} }),
  OL: () => ({ type: 'ol', attrs: {} }),
  P: () => ({ type: 'p', attrs: {} }),
  PRE: () => ({ type: 'code', attrs: {} }),
  UL: () => ({ type: 'ul', attrs: {} }),
  IFRAME: (el) => ({ type: 'embed', attrs: { url: el.getAttribute('href') } }),
  TABLE: (el) => ({ type: 'table', attrs: {} }),
  THEAD: (el) => ({ type: 'thead', attrs: {} }),
  TBODY: (el) => ({ type: 'tbody', attrs: {} }),
  TR: (el) => ({ type: 'tr', attrs: {} }),
  TD: (el) => ({ type: 'td', attrs: {} }),
  TH: (el) => ({ type: 'th', attrs: {} })
}

const TEXT_TAGS = {
  CODE: () => ({ code: true }),
  //DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
  MARK: () => ({ mark: true }),
  B: () => ({ bold: true }),
  SUP: () => ({ superscript: true }),
  SUB: () => ({ subscript: true })
}
const TEXT_TAGS_MAP = {
  CODE: 'code',
  // DEL: 'strikethrough',
  EM: 'italic',
  I: 'italic',
  S: 'strikethrough',
  STRONG: 'bold',
  U: 'underline',
  MARK: 'mark',
  B: 'bold',
  SUP: 'superscript',
  SUB: 'subscript'
}

const generateId = () => v4().split('-').join('')
const whiteCharPattern = /^[\s ]+$/
export const deserialize = (el, options, editor) => {
  if (el.nodeType === 3) {
    if (
      el.parentElement.nodeName === 'BODY'
    ) {
      return null
    }
    return el.textContent
  } else if (el.nodeType !== 1 || el.nodeName === 'META') {
    return null
  } else if (el.nodeName === 'BR') {
    return { text: "\n", break: false, seperatorId: generateId() }
  } else if (el.nodeName === 'O:P') {
    if (el.childNodes?.length) {
      return { text: '' }
    }
    return null
  }
  const { nodeName } = el
  let parent = el

  if (
    nodeName === 'PRE' &&
    el.childNodes[0] &&
    el.childNodes[0]?.nodeName === 'CODE'
  ) {
    parent = el.childNodes[0]
  }
  if (nodeName === 'DIV' && el.getAttribute('style')?.includes('mso-element:comment-list')) {
    return null
  }
  if(nodeName === 'A' &&  el.getAttribute('class')?.includes('msocomanchor')) {
    return null
  }
  if( nodeName === 'SPAN' && /mso-list:\s*Ignore/i.test(el.getAttribute('style') || '')) {
    return null
  }

  let children = Array.from(parent.childNodes).map((child) => deserialize(child, options, editor)).flat()
  children = children.filter((child) => child !== null)

  children = children.map((child) => {
    if (typeof child === 'string') {
      return child.replace(/\n/g, ' ')
    } else return child
  })

  if (children.length === 0) {
    if (nodeName === "A" && el.getAttribute('href')) {
      children = [{ text: '\n', break: false }]
    }
    else {
      children = [{ text: '' }]
    }
  }

  if (el.nodeName === 'BODY') {
    if (LIST_TYPES.includes(children?.[0]?.type)) {
      children.unshift({ type: 'p', children: [{ text: '' }] })
    }
    return jsx('fragment', {}, [...children])
  }

  const heading_pattern = /^MsoHeading/
  const list_pattern = /^MsoListParagraph/

  const [isCustomRte, customToolbarComponents, FILTERED_ELEMENT_TAGS] = getCustomToolbarComponents(editor, ELEMENT_TAGS);

  if (FILTERED_ELEMENT_TAGS[nodeName]) {
    let attrs = ELEMENT_TAGS[nodeName](el)
    if (el.getAttribute('class') === 'MsoTitle') {
      attrs = {
        type: 'h1',
        ...attrs,
        attrs: {
          ...attrs.attrs,
          style: {
            ...attrs.attrs.style,
            "text-align": 'center'
          }
        }
      }
    }

    if (el.getAttribute('align')) {
      attrs = {
        ...attrs,
        attrs: {
          ...attrs.attrs,
          style: {
            ...attrs.attrs.style,
            "text-align": el.style['text-align']
          }
        }
      }
    }

    if (el.nodeName === 'P') {
      const nodeClass = el.getAttribute('class')

      switch (nodeClass) {
        case 'MsoTitle':
          attrs.type = 'h1'
          attrs = {
            ...attrs,
            attrs: {
              ...attrs.attrs,
              style: {
                ...attrs.attrs.style,
                "text-align": 'center'
              }
            }
          }
          break
        case 'MsoSubtitle':
          attrs.type = 'h3'
          break
        case 'MsoQuote':
        case 'MsoIntenseQuote':
          attrs.type = 'blockquote'
          break
      }
    }

    if (el.nodeName === 'A' && !el.getAttribute('href')) {
      //ignoring "a" and returning it's children only
      let deserializedChildren = Array.from(el.childNodes)
      .map((child) => deserialize(child, options, editor))
      .flat()
      .filter(child => child !== null);
      
      return deserializedChildren.length ? deserializedChildren : [{ text: '' }];
    }
    // if (nodeName === 'P' && el.childNodes[0]?.nodeName === 'SPAN') {
    //   if (el.childNodes[0]?.childNodes[0]?.nodeName === 'W:SDT') {
    //     const checked = el.childNodes[0]?.childNodes[0]?.getAttribute(
    //       'checkboxischecked'
    //     )
    //     if (checked) {
    //       return jsx(
    //         'element',
    //         {
    //           type: 'check-list',
    //           attrs: {},
    //           checked: checked === 't' ? true : false
    //         },
    //         [{ text: el.childNodes[0]?.childNodes[1]?.textContent || '' }]
    //       )
    //     }
    //   }
    // }

    // if (nodeName === 'P' && heading_pattern.test(el.getAttribute('class'))) {
    //   const paragraphChild = Array.from(el.childNodes).map((child: any, index) => {
    //     if (child.nodeType === 3) {
    //       return {
    //         text: child.textContent,
    //         italic:
    //           el.getAttribute('class')[el.getAttribute('class').length - 1] %
    //           2 ===
    //           0
    //       }
    //     }
    //     return children[index]
    //   })
    //   return jsx('element', { type: 'h6', attrs: {} }, paragraphChild)
    // }

    if (nodeName === "P" && el.getAttribute?.('style')?.includes('mso-list')) {
      let listType = 'ul'
      //@ts-ignore
      let levelMatch = String(el.getAttribute?.('style')).match(
        /lfo(\d+)/
      )
      let levelMatchPrevious = String(el.previousSibling?.getAttribute?.('style')).match(
        /lfo(\d+)/
      )

      let matchCurrent = String(el.getAttribute?.('style')).match(
        /level(\d+)/
      )

      let matchNext = String(el.nextSibling?.getAttribute?.('style')).match(
        /level(\d+)/
      )

      const isMsoOl = (msoIgnoreSpan) => {
          const text = msoIgnoreSpan.textContent.trim();
          
          // Check for numbered lists (1., 2., 3., etc.)
          if (/^\d+[.)]/.test(text)) {
            return true;
          }

          // Check for alphabetical lists (a., b., c., etc. or A., B., C., etc. or a), b), etc.)
          if (/^[a-zA-Z][.)]/.test(text)) {
            return true;
          }
          
          // Check for Roman numerals (i., ii., iii., iv., v., etc. or I., II., III., etc.)
          if (/^(?=[mdclxvi])m{0,4}(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})\.$/i.test(text)) {
              return true;
          }
          
          return false;
      };

      const findMSOListElement = (el) => {
        if (!el) return false;
        if (el.nodeType !== 1) return false;
        if (typeof el.querySelectorAll !== 'function') return false;

        try{
          const spans = Array.from(el.querySelectorAll('span[style*="mso-list:" i]'))
              .filter((span : HTMLSpanElement) => /mso-list:\s*Ignore/i.test(span.style.cssText || span.getAttribute('style')));
          if (spans.length > 0) {
            return spans[0];
          } else {
            return undefined;
          }
        }catch (error) {
          console.error("Error in findMSOListElement:", error);
          return undefined;
        }
      }

      const createParagraphChild = (el: Node) => {
        let children = []
        let listElWrapper = null;
        const msoListElement = findMSOListElement(el)
        if (msoListElement) {
          listElWrapper = el.childNodes;
          if(isMsoOl(msoListElement)) listType = 'ol';
        }
        let listChildren = createMultilevelChildren(listElWrapper)
        children.push({ type: 'li', attrs: {}, children: listChildren })
        return children.filter((child) => child !== null)
      }

      const createMultilevelChildren = (childNodes: Node[] | undefined, hasNested?:boolean) => {
        let listChildren = []
        if (childNodes) {
          Array.from(childNodes).forEach((child: any, index: number) => {
            let listChild = deserialize(child, options, editor)
            if (listChild) {
              if (typeof listChild === 'string') {
                listChildren.push({ text: listChild })
              } else if (Array.isArray(listChild)) {
                listChildren = listChildren.concat(listChild);
              } else {
                listChildren.push(listChild)
              }
            }
          })
        }
        if (listChildren.length) {
          listChildren = listChildren.filter(child => {
            if (typeof child === 'object' && 'text' in child) {
              return child.text !== '' && !whiteCharPattern.test(child.text);
            }
            return true;
          });
        }
        if (isEmpty(listChildren)) listChildren.push({ text: '' })

        if(!hasNested) return listChildren;        
        return [
          {type: 'fragment', children: listChildren, attrs: {}}
        ];
      }

      const createMultilevelList = (el, lvl, rootLevel= null) => {
        let currentListItem = el
        let elLevel
        let isLastReached = false
        let children = []
        while (!isLastReached) {
          if (currentListItem?.nodeType === 3 || currentListItem?.nodeType === 8) {
            currentListItem = currentListItem.nextSibling
            continue
          }
          let levelMatch = String(currentListItem?.getAttribute('style')).match(
            /level(\d+)/
          )
          if (levelMatch) {
            elLevel = Number(levelMatch[1])

            // Stop if we encounter a sibling of the root element
            if (lvl === rootLevel && elLevel === rootLevel && children.length) {
              isLastReached = true
              break
            }

            if (elLevel === lvl) {
              children.push({
                type: 'li',
                attrs: {},
                children: createMultilevelChildren(currentListItem?.childNodes, true)
              })
              if (currentListItem?.getAttribute?.('style')?.includes('level10')) {
                isLastReached = true
                break
              }
              currentListItem = currentListItem.nextSibling
              if (!currentListItem) {
                return
              }
            } else if (elLevel > lvl) {
              let res = createMultilevelList(currentListItem, lvl + 1)
              const lastItem = children[children.length - 1];
              if (lastItem) {
                lastItem.children.push(res[0]);
              }

              currentListItem = res[1]
              isLastReached = res[2]
            } else if (elLevel < lvl) {
              break
            }
          } else {
            isLastReached = true;
            break
          }
        }
        let listType = 'ul';
        const msoListElement = findMSOListElement(el)
        if (msoListElement) {
          if(isMsoOl(msoListElement)) listType = 'ol';
        }
        return [
          jsx('element', { type: listType, attrs: {} }, children),
          currentListItem,
          isLastReached
        ]
      }
      const findIfNestedList = (elem) => {
        var sibling: any = elem
        while (sibling) {
          // Skip non-element nodes (text nodes, comments, etc.)
          if (sibling.nodeType !== 1) {
            sibling = sibling.nextSibling
            continue
          }
          
          // Check if this sibling has mso-list style
          if (sibling?.getAttribute?.('style')?.includes('mso-list')) {
            let siblingLfo = String(sibling.getAttribute?.('style')).match(
              /lfo(\d+)/
            )
            let siblingLevel = String(sibling.getAttribute?.('style')).match(
              /level(\d+)/
            )
            if (Number(siblingLfo?.[1]) === Number(levelMatch?.[1]) && Number(siblingLevel?.[1]) > 1) {
              return true
            }
          } else {
            // If we encounter a non-mso-list element, stop checking
            break
          }
          sibling = sibling.nextSibling
        }
        return false
      }
      const getNextSibling = (elem) => {
        let allLevel1Elements = []
        var sibling = elem?.nextSibling
        let levelMatchSibling = String(sibling?.getAttribute?.('style')).match(
          /lfo(\d+)/
        )
        while (sibling?.getAttribute?.('style')?.includes('mso-list')) {
          levelMatchSibling = String(sibling?.getAttribute?.('style')).match(
            /lfo(\d+)/
          )
          if (sibling?.getAttribute?.('style').includes('level1') && Number(levelMatch?.[1]) === Number(levelMatchSibling?.[1])) {
            allLevel1Elements?.push(sibling)
          }
          sibling = sibling?.nextSibling
        }
        return allLevel1Elements
      };
      if (nodeName === "P" && Number(matchCurrent?.[1]) === 1 && Number(levelMatch?.[1]) === Number(levelMatchPrevious?.[1])) {
        return null
      }
      if (nodeName === "P" && Number(matchCurrent?.[1]) === 1 && (Number(matchCurrent?.[1]) < Number(matchNext?.[1]) || findIfNestedList(el))) {
        let listType = 'ul'
        const msoListElement = findMSOListElement(el)
        if (msoListElement) {
          if(isMsoOl(msoListElement)) listType = 'ol';
        }
        children = [...createMultilevelList(el, 1, 1)[0]['children']]
        const nextSiblingNode = getNextSibling(el);
        if (nextSiblingNode?.length) {
          Array.from(nextSiblingNode).forEach((element,index) => {
            const nextSiblingChildren = createMultilevelList(element, 1, 1)[0]['children'];
            children = children.concat(nextSiblingChildren)
          })
        }
        if(children.length === 0) {
          return null
        }
        return jsx('element', { type: listType, attrs: {} }, children)
      }

      if (nodeName === "P" && Number(matchCurrent[1]) > 1) {
        return null
      }

      if (nodeName === "P" && el.getAttribute?.('style')?.includes('level1') && el.previousSibling?.getAttribute?.('style')?.includes('level1') && Number(levelMatch?.[1]) === Number(levelMatchPrevious?.[1])) {
        return null
      }
      //first list
      if (nodeName === "P" && el.getAttribute('style')?.includes('level1') && !el.previousSibling?.getAttribute?.('style')?.includes('mso-list')) {
        let multipleListChildren = createParagraphChild(el)
        if (getNextSibling(el)) {
          Array.from(getNextSibling(el)).forEach((element) => {
            multipleListChildren?.push(createParagraphChild(element))
          })
        }
        return jsx(
          'element',
          {
            type: listType,
            attrs: {}
          },
          multipleListChildren
        )
      }
      //other list
      else if (nodeName === "P" && el.getAttribute?.('style')?.includes('level1') && el.previousSibling?.getAttribute?.('style')?.includes('mso-list') && Number(levelMatch?.[1]) !== Number(levelMatchPrevious?.[1])) {
        let multipleListChildren = createParagraphChild(el)
        if (getNextSibling(el)) {
          Array.from(getNextSibling(el)).forEach((element) => {
            multipleListChildren?.push(createParagraphChild(element))
          })
        }
        return jsx(
          'element',
          {
            type: listType,
            attrs: {}
          },
          multipleListChildren
        )
      }
    }
    // if (nodeName === 'P' && list_pattern.test(el.getAttribute('class'))) {
    //   let listType = 'ul'
    //   const nodeClass = el.getAttribute('class')

    //   const createParagraphChild = (el: Node) => {
    //     let paragraphChild = Array.from(el.childNodes).map((child: any) => {
    //       if (
    //         child.nodeName === 'SPAN' &&
    //         child.childNodes[0]?.nodeName === 'SPAN' &&
    //         child.childNodes[0]?.getAttribute('style') &&
    //         /^mso-list/.test(child.childNodes[0]?.getAttribute('style'))
    //       ) {

    //         if (child?.childNodes[0]?.childNodes[0]?.nodeName === '#text') {
    //           if (
    //             !Number.isNaN(
    //               parseInt(child?.childNodes[0]?.childNodes[0]?.textContent[0])
    //             )
    //           )
    //             listType = 'ol'
    //         }
    //       }

    //       if (child.nodeType === 3) {
    //         return {
    //           text: child.textContent
    //         }
    //       } else {
    //         return null
    //       }
    //     })
    //     return paragraphChild.filter((child) => child !== null)
    //   }

    //   if (nodeClass === 'MsoListParagraph') {
    //     const listChildren = createParagraphChild(el)
    //     return jsx(
    //       'element',
    //       {
    //         type: listType,
    //         attrs: {}
    //       },
    //       { type: 'li', attrs: {}, children: listChildren }
    //     )
    //   }

    //   const createMultilevelChildren = (el: Node) => {
    //     let children = Array.from(el.childNodes).map((child) => {
    //       if (
    //         child.nodeType === 3 ||
    //         (child.nodeName === 'SPAN' && child.childNodes[0].nodeType === 3)
    //       ) {
    //         return {
    //           text: child.textContent
    //         }
    //       } else {
    //         return null
    //       }
    //     })
    //     return children.filter((child) => child !== null)
    //   }

    //   const createMultilevelList = (el, lvl, listType) => {
    //     let currentListItem = el
    //     let elLevel
    //     let isLastReached = false
    //     let children = []

    //     while (!isLastReached) {
    //       if (currentListItem.nodeType === 3) {
    //         currentListItem = currentListItem.nextSibling
    //         continue
    //       }
    //       let levelMatch = String(currentListItem.getAttribute('style')).match(
    //         /level(\d+)/
    //       )
    //       if (levelMatch) {
    //         elLevel = Number(levelMatch[1])

    //         if (elLevel === lvl) {
    //           children.push({
    //             type: 'li',
    //             attrs: {},
    //             children: createMultilevelChildren(currentListItem)
    //           })
    //           if (currentListItem.getAttribute('class')?.endsWith('Last')) {
    //             isLastReached = true
    //             break
    //           }
    //           currentListItem = currentListItem.nextSibling
    //         } else if (elLevel > lvl) {
    //           let res = createMultilevelList(currentListItem, lvl + 1, listType)

    //           children.push(res[0])
    //           currentListItem = res[1]
    //           isLastReached = res[2]
    //         } else if (elLevel < lvl) {
    //           break
    //         }
    //       } else {
    //         children.push({
    //           type: 'li',
    //           attrs: {},
    //           children: createMultilevelChildren(currentListItem)
    //         })

    //         if (currentListItem.getAttribute('class')?.endsWith('Last')) {
    //           isLastReached = true
    //           break
    //         } else {
    //           currentListItem = currentListItem.nextSibling
    //         }
    //       }
    //     }

    //     return [
    //       jsx('element', { type: listType, attrs: {} }, children),
    //       currentListItem,
    //       isLastReached
    //     ]
    //   }

    //   if (nodeClass.endsWith('First')) {
    //     let listType = 'ul'
    //     Array.from(el.childNodes).forEach((child: any) => {
    //       if (
    //         child.nodeName === 'SPAN' &&
    //         child.childNodes[0]?.nodeName === 'SPAN' &&
    //         child.childNodes[0]?.getAttribute('style') &&
    //         /^mso-list/.test(child.childNodes[0]?.getAttribute('style'))
    //       ) {
    //         if (child.childNodes[0]?.childNodes[0]?.nodeName === '#text') {
    //           if (
    //             !Number.isNaN(
    //               parseInt(child.childNodes[0]?.childNodes[0]?.textContent[0])
    //             )
    //           )
    //             listType = 'ol'
    //         }
    //       }
    //     })

    //     return createMultilevelList(el, 1, listType)[0]
    //   }

    //   if (nodeClass.endsWith('Middle') || nodeClass.endsWith('Last')) {
    //     return null
    //   }
    // }

    if (el.nodeName === 'P') {
      const nodeClass = el.getAttribute('class')

      switch (nodeClass) {
        case 'MsoTitle':
          attrs.type = 'h1'
          break
        case 'MsoSubtitle':
          attrs.type = 'h3'
          break
        case 'MsoQuote':
        case 'MsoIntenseQuote':
          attrs.type = 'blockquote'
          break
      }
    }

    if (
      el.nodeName === 'P' &&
      (el.getElementsByTagName('IMG').length ||
        el.getElementsByTagName('V:IMAGEDATA').length)
    ) {
      let image = null

      if (el.getElementsByTagName('IMG').length) {
        image = el.getElementsByTagName('IMG')[0]
      } else if (el.getElementsByTagName('V:IMAGEDATA').length) {
        image = el.getElementsByTagName('V:IMAGEDATA')[0]
      }

      let attrs = ELEMENT_TAGS['IMG'](image)
      if (attrs?.attrs?.['url'].startsWith('file:///') || !attrs?.attrs?.['url']) {
        attrs.attrs['src'] = null
      }

      return jsx('element', attrs, [{ text: '' }])
    }

    // if (attrs['type'] === 'li' && el.childNodes[0]?.nodeName === 'P') {
    //   const text = deserialize(el.childNodes[0, editor]?.childNodes[0])
    //   return jsx('element', attrs, text)
    // }

    if (nodeName === 'IMG') {
      return jsx('element', attrs, [{ text: '' }])
    }

    if (nodeName === 'TABLE') {
      let row = 0,
        col
      row = el.querySelectorAll('TR').length
      const colElementLength = el.getElementsByTagName('COLGROUP')[0]?.children?.length ?? 0
      col = Math.max(...Array.from(el.getElementsByTagName('TR') as HTMLElement[]).map(row => row.children.length), colElementLength)

      let colWidths: Array<any> = Array.from({ length: col }).fill(250)

      Array.from(el.childNodes).forEach((child: any) => {
        if (child?.nodeName === 'COLGROUP') {
          let colGroupWidth = Array<number>(col).fill(250)
          let totalWidth = parseFloat(child.getAttribute('data-width')) || col * 250
          Array.from(child.children).forEach((child: any, index) => {
            if (child?.nodeName === 'COL') {
              let width = child?.style?.width ?? '250px'
              if (width.substr(-1) === '%') {
                colGroupWidth[index] = (parseFloat(width) * totalWidth) / 100
              } else if (width.substr(-2) === 'px') {
                colGroupWidth[index] = parseFloat(width)
              }
            }
          })
          colWidths = colGroupWidth
        }
      })

      let tableHead
      let tableBody

      children.forEach((tableChild) => {
        if (tableChild?.type === 'thead') {
          tableHead = tableChild
          return
        }
        if (tableChild?.type === 'tbody') {
          tableBody = tableChild
          return
        }
      });

      let disabledCols = [...tableHead?.attrs?.disabledCols ?? [], ...tableBody?.attrs?.disabledCols ?? []]
      delete tableHead?.attrs?.disabledCols
      delete tableBody?.attrs?.disabledCols

      attrs = {
        ...attrs,
        attrs: {
          ...attrs['attrs'],
          rows: row,
          cols: col,
          colWidths: colWidths,
          ...(disabledCols.length && { disabledCols: Array.from(new Set(disabledCols)) })
        }
      }
    }

    if (["THEAD", "TBODY"].includes(nodeName)) {
      const rows = children
      const disabledCols = rows.flatMap(row => {
        const { disabledCols } = row.attrs
        delete row['attrs']['disabledCols']
        return disabledCols ?? []
      })
      attrs.attrs['disabledCols'] = disabledCols
    }

    if (nodeName === "TBODY") {

      addVoidCellsAndApplyAttributes(children)

      children = getTbodyChildren(children)
    }

    if (nodeName === "TR") {
      const cells = children.filter(child => ['th', 'td'].includes(child.type))

      const disabledCols = cells.flatMap((cell, cellIndex) => {
        let { colSpan } = cell.attrs
        if (!colSpan) return []
        colSpan = parseInt(colSpan)
        return Array(colSpan).fill(0).map((_, i) => cellIndex + i)
      })

      if (disabledCols.length)
        attrs.attrs['disabledCols'] = disabledCols
    }

    if (nodeName === "TD" || nodeName === "TH") {
      let textFormat = {}
      if (el.style?.['font-weight'] === 'bold') {
        textFormat = { ...textFormat, bold: true }
      }

      if (el.style?.['font-style'] === 'italic') {
        textFormat = { ...textFormat, italic: true }
      }

      if (el.style?.['text-decoration'] === 'underline') {
        textFormat = { ...textFormat, underline: true }
      }

      if (el.style?.['text-decoration'] === 'line-through') {
        textFormat = { ...textFormat, strikethrough: true }
      }
      const spannedAttrs = spanningAttrs(el)

      if (!isEmpty(spannedAttrs)) {
        attrs = {
          ...attrs,
          attrs: {
            ...attrs['attrs'],
            ...spannedAttrs,
            "redactor-attributes": spannedAttrs
          },
        }
      }

      let childrenCopy = cloneDeep(children)
      let finalTdChildren = childrenCopy.filter((child) => child !== undefined)

      children = finalTdChildren.map((child: any) => {
        if (typeof child === 'string' || child.nodeType === 3) return jsx('text', { ...textFormat }, child)
        else if (child.text !== undefined) {
          const newChild = {
            ...child,
            attrs: { ...child.attrs },
            ...textFormat
          }
          return newChild
        } else {
          return child
        }
      })

      const { colSpan = 1, rowSpan } = attrs?.['attrs']

      const tableCell = [
        jsx('element', attrs, children),
        ...Array(colSpan - 1)
          .fill(0)
          .map((_) => emptyCell(nodeName.toLowerCase(), rowSpan ? { inducedRowSpan: rowSpan } : {}))
      ]
      return tableCell
    }
    if (el?.childNodes[0]?.nodeName === 'W:SDT') {
      const checked = el.childNodes[0].getAttribute('checkboxischecked')
      if (checked) {
        return jsx(
          'element',
          {
            type: 'check-list',
            attrs: {},
            checked: checked === 't' ? true : false
          },
          [{ text: children[1] }]
        )
      }
    }

    if (el?.childNodes[0]?.nodeName === 'W:SDT') {
      const checked = el.childNodes[0].getAttribute('checkboxischecked')
      if (checked) {
        return jsx(
          'element',
          {
            type: 'check-list',
            attrs: {},
            checked: checked === 't' ? true : false
          },
          [{ text: children[1] }]
        )
      }
    }

    return jsx('element', attrs, children)
  }

  if (ELEMENT_TAGS[nodeName] && !FILTERED_ELEMENT_TAGS[nodeName]) {
    if (nodeName === 'A') {
      if (children.length > 0 && typeof children[0] === 'string') {
        return children
      }
    }
    let attrs = ELEMENT_TAGS['P']()
    return jsx("element", attrs, children)
  }

  const FILTERED_TEXT_TAGS = cloneDeep(TEXT_TAGS);

  if (isCustomRte) {
    Object.keys(FILTERED_TEXT_TAGS).forEach((key) => {
      if (!customToolbarComponents.includes(TEXT_TAGS_MAP[key])) {
        delete FILTERED_TEXT_TAGS[key]
      }
    })
  }

  if (FILTERED_TEXT_TAGS[nodeName]) {
    const attrs = FILTERED_TEXT_TAGS[nodeName](el)
    return children.map((child) => jsx('text', attrs, child))
  }

  if (el?.nodeName === 'SPAN') {
    let attrs = { style: {} }
    let textFormat = {}
    const skipStyles = {
      color: false,
    }
    if (options && options.disableColor) {
      skipStyles.color = true
    }
    children = children.map((child) => {
      if (typeof child === 'string') {
        return child.replace(/\n/g, ' ')
      } else return child
    })
    if (el.style['color'] && el.closest('a') === null && !skipStyles.color) {
      attrs = {
        ...attrs,
        style: {
          ...attrs.style,
          color: el.style['color']
        }
      }
    }
    if (
      el.parentNode?.nodeName === 'P' &&
      el.parentNode?.getAttribute('class') === 'MsoNormal'
    ) {
      let nodeClass = el.getAttribute('class')

      if (
        nodeClass === 'MsoSubtleEmphasis' ||
        nodeClass === 'MsoIntenseEmphasis'
      ) {
        textFormat = { ...textFormat, italic: true }
      }
    }

    children = children.map((child) => {
      if (typeof child === 'string' || child.nodeType === 3)
        return jsx('text', { attrs, ...textFormat }, child)
      else if (child.text !== undefined) {
        const newChild = {
          ...child,
          attrs: { ...child.attrs, ...attrs },
          ...textFormat
        }

        return newChild
      } else {
        return child
      }
    })
  }

  return children
}